//////////////////////////////////////////////////////////////////////////////
//
//  알림 템플릿 추가/수정 팝업
//
//  작성자: MediInStep Team
//  생성일: 2025-12-29
//
//  설명: 알림 템플릿 등록 및 수정을 위한 팝업 다이얼로그
//
//////////////////////////////////////////////////////////////////////////////

import { Component } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { request } from "app/network";
import Swal from "sweetalert2";
import { MaterialUI } from "context";

interface AddTemplateDialogProps {
    open: boolean;
    editingTemplate?: any | null;
    onClose: () => void;
    onSuccess: () => void;
    darkMode?: boolean;
    theme?: any;
}

interface AddTemplateDialogState {
    code: string;
    name: string;
    type: string;
    titleTemplate: string;
    bodyTemplate: string;
    isActive: boolean;
    loading: boolean;
    darkMode: boolean;
    tenantSeqId: number | null;
    seqId?: number | null;
    templateCodes: Array<{ value: string; label: string }>;
}

const NOTIFICATION_TYPES = [
    { value: "PUSH", label: "푸시 알림" },
    { value: "SMS", label: "SMS" },
    { value: "EMAIL", label: "이메일" },
];

class AddTemplateDialogClass extends Component<AddTemplateDialogProps, AddTemplateDialogState> {
    static contextType = MaterialUI;

    constructor(props: AddTemplateDialogProps) {
        super(props);
        const initialDarkMode = props.darkMode ?? false;

        this.state = {
            code: "",
            name: "",
            type: "PUSH",
            titleTemplate: "",
            bodyTemplate: "",
            isActive: true,
            loading: false,
            darkMode: initialDarkMode,
            tenantSeqId: null,
            seqId: null,
            templateCodes: [],
        };
    }

    async componentDidMount() {
        // 로그인 시 선택한 테넌트 가져오기
        const SELECTED_TENANT_KEY = "mediinstep_selected_tenant";
        const savedTenantId = localStorage.getItem(SELECTED_TENANT_KEY);

        if (savedTenantId) {
            this.setState({ tenantSeqId: parseInt(savedTenantId, 10) });
        }

        // Load template codes from CommonCode API
        await this.loadTemplateCodes();
    }

    async loadTemplateCodes() {
        try {
            console.log("Loading template codes from CommonCode API...");
            const response = await request("CommonCode.getByGroup", {
                pathParams: { groupCode: "NOTIFICATION_TEMPLATE_CODE" },
            });

            console.log("CommonCode API response:", response);

            // Handle both direct array and wrapped object responses
            const data = Array.isArray(response) ? response : (response?.data || []);

            if (Array.isArray(data) && data.length > 0) {
                const codes = data.map((item: any) => ({
                    value: item.code,
                    label: `${item.code} - ${item.name}`,
                }));
                console.log("Mapped template codes:", codes);
                this.setState({ templateCodes: codes });
            } else {
                console.warn("No template codes found. Response:", response, "Data:", data);
            }
        } catch (error) {
            console.error("Failed to load template codes:", error);
        }
    }

    componentDidUpdate(prevProps: AddTemplateDialogProps) {
        // props에서 darkMode 변경 감지
        const context = this.context as any;
        const currentDarkMode = this.props.darkMode ?? context?.darkMode ?? false;

        if (currentDarkMode !== this.state.darkMode) {
            this.setState({ darkMode: currentDarkMode });
        }

        if (this.props.open && !prevProps.open) {
            // 다이얼로그가 열릴 때 필드 초기화 또는 수정 데이터 로드
            const SELECTED_TENANT_KEY = "mediinstep_selected_tenant";
            const savedTenantId = localStorage.getItem(SELECTED_TENANT_KEY);
            const tenantSeqId = savedTenantId ? parseInt(savedTenantId, 10) : this.state.tenantSeqId;

            if (this.props.editingTemplate) {
                // 수정 모드
                const template = this.props.editingTemplate;
                this.setState({
                    seqId: template.seqId,
                    code: template.code || "",
                    name: template.name || "",
                    type: template.type || "PUSH",
                    titleTemplate: template.titleTemplate || "",
                    bodyTemplate: template.bodyTemplate || "",
                    isActive: template.isActive ?? true,
                    loading: false,
                    darkMode: currentDarkMode,
                    tenantSeqId: template.tenantSeqId || tenantSeqId,
                });
            } else {
                // 추가 모드
                this.setState({
                    seqId: null,
                    code: "",
                    name: "",
                    type: "PUSH",
                    titleTemplate: "",
                    bodyTemplate: "",
                    isActive: true,
                    loading: false,
                    darkMode: currentDarkMode,
                    tenantSeqId: tenantSeqId,
                });
            }
        }
    }

    handleChange = (field: keyof AddTemplateDialogState, value: any) => {
        this.setState({ [field]: value } as any);
    };

    handleSubmit = async () => {
        const { code, name, type, titleTemplate, bodyTemplate, isActive, tenantSeqId, seqId } = this.state;
        const { theme, darkMode: propsDarkMode } = this.props;
        const { darkMode: stateDarkMode } = this.state;
        const context = this.context as any;
        const finalDarkMode = propsDarkMode ?? stateDarkMode ?? context?.darkMode ?? false;

        // 필수 값 체크
        if (!tenantSeqId) {
            this.showAlert("입력 오류", "테넌트를 선택해주세요.", "error", finalDarkMode, theme);
            return;
        }

        if (!code.trim()) {
            this.showAlert("입력 오류", "템플릿 코드를 입력해주세요.", "error", finalDarkMode, theme);
            return;
        }

        if (!name.trim()) {
            this.showAlert("입력 오류", "템플릿 이름을 입력해주세요.", "error", finalDarkMode, theme);
            return;
        }

        if (!titleTemplate.trim()) {
            this.showAlert("입력 오류", "제목 템플릿을 입력해주세요.", "error", finalDarkMode, theme);
            return;
        }

        if (!bodyTemplate.trim()) {
            this.showAlert("입력 오류", "본문 템플릿을 입력해주세요.", "error", finalDarkMode, theme);
            return;
        }

        try {
            this.setState({ loading: true });

            const param = {
                TenantSeqId: tenantSeqId,
                Code: code,
                Name: name,
                Type: type,
                TitleTemplate: titleTemplate,
                BodyTemplate: bodyTemplate,
                IsActive: isActive,
            };

            if (seqId) {
                // 수정 모드
                console.log("Updating template with param:", param);
                await request("NotificationTemplate.update", {
                    paramz: param,
                    pathParams: { id: seqId.toString() }
                });
                this.showAlert("수정 완료", "알림 템플릿이 성공적으로 수정되었습니다.", "success", finalDarkMode, theme, () => {
                    this.props.onSuccess();
                    this.props.onClose();
                });
            } else {
                // 추가 모드
                console.log("Creating template with param:", param);
                await request("NotificationTemplate.create", { paramz: param });
                this.showAlert("등록 완료", "알림 템플릿이 성공적으로 등록되었습니다.", "success", finalDarkMode, theme, () => {
                    this.props.onSuccess();
                    this.props.onClose();
                });
            }

        } catch (error: any) {
            console.error("알림 템플릿 저장 실패:", error);
            const errorMessage = error.response?.data?.message || "알림 템플릿 저장 중 오류가 발생했습니다.";
            this.showAlert("저장 실패", errorMessage, "error", finalDarkMode, theme);
        } finally {
            this.setState({ loading: false });
        }
    };

    showAlert = (title: string, text: string, icon: "success" | "error" | "warning" | "info", darkMode: boolean, theme: any, callback?: () => void) => {
        Swal.fire({
            icon: icon,
            title: title,
            text: text,
            confirmButtonText: "확인",
            background: darkMode ? theme?.palette?.background?.default || "#1a1a1a" : "#ffffff",
            color: darkMode ? "#ffffff" : "#000000",
            confirmButtonColor: theme?.palette?.info?.main || "#1976d2",
            customClass: {
                container: "swal2-container-high-zindex",
            },
            didOpen: () => {
                const swalContainer = document.querySelector(".swal2-container-high-zindex") as HTMLElement;
                if (swalContainer) {
                    swalContainer.style.zIndex = "1400";
                }
            },
        }).then(() => {
            if (callback) callback();
        });
    };

    render() {
        const { open, onClose, theme, darkMode: propsDarkMode } = this.props;
        const { code, name, type, titleTemplate, bodyTemplate, isActive, loading, darkMode: stateDarkMode } = this.state;

        const context = this.context as any;
        const finalDarkMode = propsDarkMode ?? stateDarkMode ?? context?.darkMode ?? false;

        const backgroundColor = finalDarkMode ? "#1a2035" : "#ffffff";
        const inputBackgroundColor = finalDarkMode ? "#1a2035" : "#ffffff";

        const inputSx = {
            "& .MuiOutlinedInput-root": {
                backgroundColor: `${inputBackgroundColor} !important`,
                "& fieldset": {
                    borderColor: `${finalDarkMode ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)"} !important`,
                },
                "&:hover fieldset": {
                    borderColor: `${finalDarkMode ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)"} !important`,
                },
                "&.Mui-focused fieldset": {
                    borderColor: `${theme?.palette?.info?.main || "#1976d2"} !important`,
                },
            },
            "& .MuiInputLabel-root": {
                color: `${finalDarkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)"} !important`,
            },
            "& .MuiInputBase-input": {
                color: `${finalDarkMode ? "#ffffff" : "#000000"} !important`,
                backgroundColor: "transparent !important",
            },
        };

        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { backgroundColor: backgroundColor },
                }}
            >
                <DialogTitle sx={{ backgroundColor: backgroundColor, padding: "24px 24px 16px 24px" }}>
                    <MDTypography component="span" fontWeight="medium" sx={{ fontSize: "1.25rem" }}>
                        {this.state.seqId ? '알림 템플릿 수정' : '알림 템플릿 추가'}
                    </MDTypography>
                </DialogTitle>
                <DialogContent sx={{ backgroundColor: backgroundColor, padding: "16px 24px" }}>
                    <MDBox pt={1} pb={1}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel
                                        sx={{
                                            color: `${finalDarkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)"} !important`,
                                            "&.Mui-focused": {
                                                color: `${theme?.palette?.info?.main || "#1976d2"} !important`,
                                            },
                                        }}
                                    >
                                        템플릿 코드 *
                                    </InputLabel>
                                    <Select
                                        value={code}
                                        label="템플릿 코드 *"
                                        onChange={(e) => this.handleChange("code", e.target.value)}
                                        disabled={!!this.state.seqId}
                                        sx={{
                                            backgroundColor: `${inputBackgroundColor} !important`,
                                            color: finalDarkMode ? "#ffffff" : "#000000",
                                            height: "45px",
                                            "& .MuiOutlinedInput-notchedOutline": {
                                                borderColor: `${finalDarkMode ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)"} !important`,
                                            },
                                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                                borderColor: `${finalDarkMode ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)"} !important`,
                                            },
                                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                borderColor: `${theme?.palette?.info?.main || "#1976d2"} !important`,
                                            },
                                            "&.Mui-disabled": {
                                                backgroundColor: `${finalDarkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)"} !important`,
                                            },
                                            "& .MuiSvgIcon-root": {
                                                color: finalDarkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                            },
                                        }}
                                    >
                                        {this.state.templateCodes.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <MDInput
                                    label="템플릿 이름 *"
                                    fullWidth
                                    value={name}
                                    onChange={(e: any) => this.handleChange("name", e.target.value)}
                                    sx={inputSx}
                                    autoComplete="off"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel
                                        sx={{
                                            color: `${finalDarkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)"} !important`,
                                            "&.Mui-focused": {
                                                color: `${theme?.palette?.info?.main || "#1976d2"} !important`,
                                            },
                                        }}
                                    >
                                        알림 타입 *
                                    </InputLabel>
                                    <Select
                                        value={type}
                                        label="알림 타입 *"
                                        onChange={(e) => this.handleChange("type", e.target.value)}
                                        sx={{
                                            backgroundColor: `${inputBackgroundColor} !important`,
                                            color: finalDarkMode ? "#ffffff" : "#000000",
                                            height: "45px",
                                            "& .MuiOutlinedInput-notchedOutline": {
                                                borderColor: `${finalDarkMode ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)"} !important`,
                                            },
                                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                                borderColor: `${finalDarkMode ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)"} !important`,
                                            },
                                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                borderColor: `${theme?.palette?.info?.main || "#1976d2"} !important`,
                                            },
                                            "& .MuiSvgIcon-root": {
                                                color: finalDarkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                            },
                                        }}
                                    >
                                        {NOTIFICATION_TYPES.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <MDInput
                                    label="제목 템플릿 *"
                                    fullWidth
                                    value={titleTemplate}
                                    onChange={(e: any) => this.handleChange("titleTemplate", e.target.value)}
                                    sx={inputSx}
                                    helperText="변수 사용 예: {UserName}님, 포인트가 적립되었습니다!"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <MDInput
                                    label="본문 템플릿 *"
                                    fullWidth
                                    multiline
                                    rows={4}
                                    value={bodyTemplate}
                                    onChange={(e: any) => this.handleChange("bodyTemplate", e.target.value)}
                                    sx={inputSx}
                                    helperText="변수 사용 예: {VideoTitle} 영상 시청으로 {Points}P가 적립되었습니다."
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={isActive}
                                            onChange={(e) => this.handleChange("isActive", e.target.checked)}
                                            color="info"
                                        />
                                    }
                                    label={
                                        <MDTypography variant="button" fontWeight="regular" color="text">
                                            {isActive ? "활성" : "비활성"}
                                        </MDTypography>
                                    }
                                />
                            </Grid>
                        </Grid>
                    </MDBox>
                </DialogContent>
                <DialogActions sx={{ backgroundColor: backgroundColor, padding: "16px 24px" }}>
                    <MDButton onClick={onClose} color="dark">
                        취소
                    </MDButton>
                    <MDButton
                        variant="gradient"
                        color="info"
                        onClick={this.handleSubmit}
                        disabled={loading}
                    >
                        {loading ? "저장 중..." : (this.state.seqId ? "수정" : "등록")}
                    </MDButton>
                </DialogActions>
            </Dialog>
        );
    }
}

export default AddTemplateDialogClass;
